import React, { useState, useRef, useEffect, useCallback } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { House, BookOpen, Plus, Users, User, ChevronUp } from 'lucide-react';
import { isRouteAllowed } from '../config/tiers';

const MENU_SECTIONS = [
  { label: 'Kitchen', links: [
    { to: '/', label: 'Home' },
    { to: '/inventory', label: 'Inventory' },
    { to: '/recipes', label: 'Recipes' },
    { to: '/catalog', label: 'Catalog' },
    { to: '/shopping-list', label: 'Shopping' },
  ]},
  { label: 'Nutrition', links: [
    { to: '/meal-planner', label: 'Meal Plan' },
    { to: '/macro-planner', label: 'Macros' },
    { to: '/meal-synthesizer', label: 'Synthesizer' },
    { to: '/budget', label: 'Budget' },
  ]},
  { label: 'Protocol', links: [
    { to: '/biometrics', label: 'Biometrics' },
    { to: '/workout-protocol', label: 'Workout' },
    { to: '/calendar', label: 'Calendar' },
    { to: '/progressive-overload', label: 'Overload' },
  ]},
  { label: 'Community', links: [
    { to: '/community', label: 'Feed' },
    { to: '/discover', label: 'Discover' },
    { to: '/profile', label: 'My Profile' },
  ]},
  { label: 'Tools', links: [
    { to: '/fitness', label: 'Fitness Log' },
    { to: '/brigade', label: 'Brigade' },
    { to: '/ai-recipe', label: 'Recipe AI' },
    { to: '/blog', label: 'Protocol Library' },
    { to: '/feedback', label: 'Feedback' },
    { to: '/upgrade', label: 'Plans' },
    { to: '/settings', label: 'Settings' },
  ]},
];

const SWIPE_UP_THRESHOLD = 40;
const SWIPE_MAX_X_DRIFT = 30;
const HINT_KEY = 'dP_swipeHintSeen';

const HomeTab = ({ onSwipeUp }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const touchStartY = useRef(null);
  const touchStartX = useRef(null);
  const isActive = location.pathname === '/';

  const handleTouchStart = (e) => {
    touchStartY.current = e.touches[0].clientY;
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartY.current === null) return;
    const dy = touchStartY.current - e.changedTouches[0].clientY;
    const dx = Math.abs(e.changedTouches[0].clientX - touchStartX.current);
    touchStartY.current = null;
    touchStartX.current = null;

    if (dy > SWIPE_UP_THRESHOLD && dx < SWIPE_MAX_X_DRIFT) {
      e.preventDefault();
      if (navigator.vibrate) navigator.vibrate(15);
      onSwipeUp();
    } else if (Math.abs(dy) < 8 && dx < 8) {
      navigate('/');
    }
  };

  return (
    <button
      className={`mobile-nav-tab${isActive ? ' active' : ''}`}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onClick={() => navigate('/')}
      aria-label="Home — swipe up for all features"
    >
      <span className="mobile-nav-icon"><House size={22} aria-hidden="true" /></span>
      <span className="mobile-nav-label">Home</span>
      <span className="mobile-nav-swipe-hint"><ChevronUp size={10} aria-hidden="true" /></span>
    </button>
  );
};

const MobileNav = ({ tier, hidden }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!localStorage.getItem(HINT_KEY)) {
      const t = setTimeout(() => setShowHint(true), 1200);
      return () => clearTimeout(t);
    }
  }, []);

  const dismissHint = useCallback(() => {
    setShowHint(false);
    localStorage.setItem(HINT_KEY, '1');
  }, []);

  const handleNavigate = (to) => {
    setMenuOpen(false);
    navigate(to);
  };

  return (
    <>
      {menuOpen && (
        <>
          <div className="longpress-backdrop" onClick={() => setMenuOpen(false)} />
          <div className="longpress-menu">
            <div className="longpress-menu-inner">
              {MENU_SECTIONS.map(({ label, links }) => (
                <div key={label} className="longpress-section">
                  <div className="longpress-section-label">{label}</div>
                  <div className="longpress-section-links">
                    {links.map(({ to, label: linkLabel }) => {
                      const allowed = isRouteAllowed(tier, to);
                      return (
                        <button
                          key={to}
                          className={`longpress-link${!allowed ? ' locked' : ''}`}
                          onClick={() => handleNavigate(to)}
                        >
                          {linkLabel}
                          {!allowed && <span className="longpress-lock">PRO</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {showHint && !menuOpen && (
        <div className="longpress-hint" onClick={dismissHint} role="tooltip">
          <div className="longpress-hint-text">
            ↑ Swipe up on <strong>Home</strong> to see all features
          </div>
          <div className="longpress-hint-arrow" />
        </div>
      )}

      <nav className={`mobile-nav${hidden ? ' nav-hidden' : ''}`} role="navigation" aria-label="Mobile navigation">
        <HomeTab onSwipeUp={() => { setMenuOpen(true); dismissHint(); }} />
        <NavLink to="/recipes" className={({ isActive }) => `mobile-nav-tab${isActive ? ' active' : ''}`}>
          <span className="mobile-nav-icon"><BookOpen size={22} aria-hidden="true" /></span>
          <span className="mobile-nav-label">Recipes</span>
        </NavLink>
        <NavLink to="/community/new" className={({ isActive }) => `mobile-nav-tab mobile-nav-create${isActive ? ' active' : ''}`}>
          <span className="mobile-nav-icon"><Plus size={24} aria-hidden="true" /></span>
          <span className="mobile-nav-label">Post</span>
        </NavLink>
        <NavLink to="/community" className={({ isActive }) => `mobile-nav-tab${isActive ? ' active' : ''}`}>
          <span className="mobile-nav-icon"><Users size={22} aria-hidden="true" /></span>
          <span className="mobile-nav-label">Community</span>
        </NavLink>
        <NavLink to="/profile" className={({ isActive }) => `mobile-nav-tab${isActive ? ' active' : ''}`}>
          <span className="mobile-nav-icon"><User size={22} aria-hidden="true" /></span>
          <span className="mobile-nav-label">Profile</span>
        </NavLink>
      </nav>
    </>
  );
};

export default MobileNav;
